import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Content from '../../../styled-components/components/Content';
import DetailBox from '../../../styled-components/components/DetailBox';
import { H4 } from '../../../styled-components/components/Heading';
import InputGroup from '../../../styled-components/components/InputGroup';
import InputHelp from '../../../styled-components/components/InputHelp';
import InputLabel from '../../../styled-components/components/InputLabel';
import InputRadio from '../../../styled-components/components/InputRadio';
import InputSelect from '../../../styled-components/components/InputSelect';
import InputText from '../../../styled-components/components/InputText';
import {
  KeyValueList,
  KeyValueListItem,
  KeyValueListKey,
  KeyValueListValue,
} from '../../../styled-components/components/KeyValueList';
import Modal from '../../../styled-components/components/Modal';
import { ModalAccentSection } from '../../../styled-components/components/Modal/wrapper';
import { Skeleton } from '../../../styled-components/components/Skeleton';
import { Stack, StackLarge, StackSmall } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';

import { bindActionCreators } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import type { EvCalculationChargingPerKilometer } from '../../../@types/EvCalculation';
import type Item from '../../../@types/item';
import useEvCharchingCostPerKilometer from '../../../Data/EvCalculation/useEvCharchingCostPerKilometer';
import type { ApiResponse } from '../../../Http';
import {
  trackChargingCostDetailsClick as _trackChargingCostDetailsClick,
  trackChargingCostParameterChange as _trackChargingCostParameterChange,
} from '../../../actions/tracking';
import RangeSliderSimple from '../../../components/RangeSlider/RangeSliderSimple';
import { numberSeparator } from '../../../helpers/formats';
import { prettify } from '../../../helpers/prices';
import useDebounce from '../../../hooks/useDebounce';
import { getMarket } from '../../../i18n';
import Alert from '../../../styled-components/components/Alert';

const calculateCostPerMonth = (costPerKilometer: number | undefined, distance: number) => {
  if (!costPerKilometer) return undefined;
  return (costPerKilometer * distance * 10) / 12;
};

interface ChargingCostModalProps {
  id: string;
  defaultData: ApiResponse<EvCalculationChargingPerKilometer>;
  distance: number;
  costPerMonth?: number;
  distanceOptions: number[];
  item: Item;
  customPriceAsDefault?: boolean;
  onDistanceUpdate: (distance: number) => void;
  onDistanceChangeSelect: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onClose: () => void;
}

const ChargingCostModal = ({
  id,
  costPerMonth,
  defaultData,
  distance,
  distanceOptions,
  item,
  customPriceAsDefault,
  onDistanceUpdate,
  onDistanceChangeSelect,
  onClose,
}: ChargingCostModalProps) => {
  const { t } = useTranslation(['common', 'glossary', 'item']);
  const [mounted, setHasMounted] = useState(false);
  const dispatch = useDispatch();
  const trackChargingCostParameterChange = bindActionCreators(
    _trackChargingCostParameterChange,
    dispatch,
  );
  const trackChargingCostDetailsClick = bindActionCreators(
    _trackChargingCostDetailsClick,
    dispatch,
  );
  const [kwh, kwhSearchInOre, setKwh] = useDebounce(
    250,
    defaultData.response.description.costPerKwh.value * 100,
  );
  const [custom, setCustom] = useState(customPriceAsDefault || false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const [customData, request] = useEvCharchingCostPerKilometer(
    id,
    kwhSearchInOre / 100,
    !custom || Number.isNaN(kwhSearchInOre),
  );

  const result = useMemo(
    () => (custom ? customData : defaultData)?.response,
    [defaultData, customData, custom, distance],
  );

  const costPerKilometer = result?.description.costPerKilometer.value;
  const costPerMonthCustom = useMemo(
    () => calculateCostPerMonth(costPerKilometer, distance),
    [costPerKilometer, distance],
  );

  const debounceLoading = kwh === kwhSearchInOre;

  const onDistanceAfterChange = useCallback(
    (nextDistance: number) => {
      trackChargingCostParameterChange({
        item,
        information: {
          parameter: 'distance-slider',
          distance: `${nextDistance}`,
          context: 'modal',
          chargingCost: calculateCostPerMonth(costPerKilometer, nextDistance),
        },
      });
    },
    [costPerKilometer],
  );

  useEffect(() => {
    if (mounted && custom && customData?.response) {
      const costPerKilometerCustom = customData.response?.description.costPerKilometer.value;
      trackChargingCostParameterChange({
        item,
        information: {
          parameter: 'distance-slider',
          distance: `${distance}`,
          context: 'modal',
          chargingCost: calculateCostPerMonth(costPerKilometerCustom, distance),
          electricityCost: kwhSearchInOre,
        },
      });
    }
  }, [customData]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setKwh(Number.parseInt(e.currentTarget.value, 10)),
    [],
  );

  const onRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCustom(e.currentTarget.value === '1'),
    [],
  );

  const onMoreInformation = useCallback((open: boolean) => {
    if (open) {
      trackChargingCostDetailsClick({
        information: {
          distance: `${distance}`,
          context: 'modal',
          chargingCost: calculateCostPerMonth(costPerKilometer, distance),
        },
      });
    }
  }, []);

  const marketCode = getMarket().marketCode;
  const mileageMultiplier = marketCode === 'NO' ? 10 : 1;

  return (
    <Modal onClose={onClose} heading={t('chargingCost.calculateChargingCost', { ns: 'item' })}>
      <StackLarge>
        <Content fullWidth>
          <p>{t('chargingCost.calculateChargingCostModalDescription', { ns: 'item' })}</p>
        </Content>
      </StackLarge>
      <StackLarge>
        <Stack>
          <InputLabel htmlFor="charging-cost-modal-input-mileage">
            {t('chargingCost.yourAnnualMileage', { ns: 'item' })}
          </InputLabel>
          <StackSmall>
            <InputSelect
              id="charging-cost-modal-input-mileage"
              title={t('chargingCost.yourAnnualMileage', { ns: 'item' })}
              value={distance}
              onChange={onDistanceChangeSelect}
              options={distanceOptions.map((d) => ({
                value: d,
                displayName: prettify(
                  d * mileageMultiplier,
                  t('mileageUnit', { ns: 'glossary', count: 2 }),
                ),
              }))}
            />
          </StackSmall>
          <StackSmall>
            <RangeSliderSimple
              step={500}
              min={500}
              max={5000}
              value={distance}
              onChange={onDistanceUpdate}
              onAfterChange={onDistanceAfterChange}
            />
          </StackSmall>
        </Stack>
        <Stack>
          <InputGroup label={t('chargingCost.whatPriceToUse', { ns: 'item' })}>
            <InputRadio
              name="input-radio-electricy-price"
              id="radio-standard-price"
              checked={!custom}
              value={0}
              onChange={onRadioChange}
            >
              {t('chargingCost.standardPrice', { ns: 'item' })}
            </InputRadio>
            <InputRadio
              name="input-radio-electricy-price"
              id="radio-custom-price"
              checked={custom}
              value={1}
              onChange={onRadioChange}
            >
              {t('chargingCost.customPrice', { ns: 'item' })}
            </InputRadio>
          </InputGroup>
        </Stack>
        {custom && (
          <Stack>
            <InputLabel htmlFor="input-electricity-price-value">
              {t('chargingCost.yourPrice.label', { ns: 'item' })}
            </InputLabel>
            <InputText
              type="number"
              value={Number.isNaN(kwh) ? '' : Math.round(kwh)}
              min={0}
              step={1}
              disabled={!custom}
              placeholder={t('chargingCost.yourPrice.placeholder', { ns: 'item' })}
              unit={t('chargingCost.electricPriceUnit', { ns: 'item' })}
              id="input-electricity-price-value"
              onChange={onChange}
            />
            <InputHelp>{t('chargingCost.yourPrice.help', { ns: 'item' })}</InputHelp>
          </Stack>
        )}
        {request.isError && debounceLoading && (
          <Stack>
            <Alert tone="error">{t('chargingCost.couldNotFetchData', { ns: 'item' })}</Alert>
          </Stack>
        )}
      </StackLarge>
      <ModalAccentSection>
        <DetailBox
          buttonTextDefault={t('moreInformation', { ns: 'common' })}
          buttonTextExtended={t('lessInformation', { ns: 'common' })}
          onClick={onMoreInformation}
          details={
            <KeyValueList $size="small">
              <KeyValueListItem>
                <KeyValueListKey>
                  {t('chargingCost.vehicleBatteryCapacity', { ns: 'item' })}
                </KeyValueListKey>
                <KeyValueListValue>
                  {defaultData.response.vehicle.batteryCapacity.value}{' '}
                  {defaultData.response.vehicle.batteryCapacity.unit}
                </KeyValueListValue>
              </KeyValueListItem>
              <KeyValueListItem>
                <KeyValueListKey>{t('chargingCost.vehicleRange', { ns: 'item' })}</KeyValueListKey>
                <KeyValueListValue>
                  {defaultData.response.vehicle.electricalRangeWLTP.value}{' '}
                  {defaultData.response.vehicle.electricalRangeWLTP.unit}
                </KeyValueListValue>
              </KeyValueListItem>
              <KeyValueListItem>
                <KeyValueListKey>
                  {t('chargingCost.indicatedAnnualMileage', { ns: 'item' })}
                </KeyValueListKey>
                <KeyValueListValue>
                  {numberSeparator(distance)}{' '}
                  {t('mileageUnit', { ns: 'glossary', count: distance })}
                </KeyValueListValue>
              </KeyValueListItem>
              {!custom && (
                <>
                  <KeyValueListItem>
                    <KeyValueListKey>
                      {t('chargingCost.standardPriceIncludingTax', { ns: 'item' })}
                    </KeyValueListKey>
                    <KeyValueListValue>
                      {result && debounceLoading
                        ? prettify(
                            result.description.costPerKwh.value * 100,
                            t('chargingCost.electricPriceUnit', { ns: 'item' }),
                          )
                        : t('Loading...', { ns: 'common' })}
                    </KeyValueListValue>
                  </KeyValueListItem>
                  <KeyValueListItem>
                    <KeyValueListKey>
                      {t('chargingCost.electricityCharge', { ns: 'item' })}
                    </KeyValueListKey>
                    <KeyValueListValue>
                      {t('chargingCost.notIncludedInCalculation', { ns: 'item' })}
                    </KeyValueListValue>
                  </KeyValueListItem>
                </>
              )}
            </KeyValueList>
          }
        >
          <StackSmall>
            <H4 as="div" $noMargin>
              {custom ? (
                kwh !== kwhSearchInOre ? (
                  <Skeleton $width="115px" $height="18px" $inlineBlock />
                ) : costPerMonthCustom ? (
                  t('Price per month', { ns: 'common', count: costPerMonthCustom })
                ) : (
                  <Skeleton $width="115px" $height="18px" $inlineBlock />
                )
              ) : costPerMonth ? (
                t('Price per month', { ns: 'common', count: costPerMonth })
              ) : (
                <Skeleton $width="115px" $height="18px" $inlineBlock />
              )}
              *
            </H4>
          </StackSmall>
          <StackSmall>
            {custom ? (
              <Text $size="small" $tone="alt">
                *{t('chargingCost.customPriceDisclaimer', { ns: 'item' })}
              </Text>
            ) : (
              <Text $size="small" $tone="alt">
                *{t('chargingCost.standardPriceDisclaimer', { ns: 'item' })}
              </Text>
            )}
          </StackSmall>
        </DetailBox>
      </ModalAccentSection>
    </Modal>
  );
};

export default ChargingCostModal;
